import { BrewManApiError, BrewManError } from "@/app/errors";
import Guid from "@/app/types/common/guid";
import { tracer } from "@/app/tracing";
import { computed, reactive, readonly } from "vue";
import { useI18n } from "@/plugins/i18n";
import type { SnackbarConfiguration } from "@/components/controls/snackbar/SnackbarTypes";
import { EnumSnackbarType } from "@/components/controls/snackbar/SnackbarTypes";
import environment from "@/environment";
import { EnumUserPermission } from "@/app/services/generated/enums/enumUserPermission.generated";

const { $m } = useI18n();

interface AppStoreState {
  version: string;
  contextualHelpLink: string;
  loading: boolean;
  entryCount: number;
  error: string | Error | null;
  unauthorisedError: EnumUserPermission | null;
  appStateKey: string;
  documentTitle: string;
  snackbarConfigs: SnackbarConfiguration[];
}

const state = reactive<AppStoreState>({
  version: "1",
  contextualHelpLink: "",
  loading: false,
  entryCount: 0,
  error: null,
  appStateKey: Guid.create().toString(),
  documentTitle: $m.default_document_title,
  snackbarConfigs: [],
  unauthorisedError: null,
});

function setLoading(loading: boolean): void {
  if (loading) {
    tracer.pushSpan(false, "loading", { initiator: "loading" });
  } else {
    tracer.popSpan({ initiator: "loading" });
  }
  state.entryCount = state.entryCount + (loading ? 1 : -1);
  state.loading = state.entryCount > 0;
}

/* eslint-disable no-console */
function showError(error: string | Error | null, info?: string): void {
  // Special case for null or string
  if (!error || typeof error === "string") {
    state.error = error;
    return;
  }
  // Logging to the console allows Chrome to do it's thang with sourcemaps
  if (window.console && console.error) {
    console.error(error);
    console.log({ info });
  }

  // Do not present user with unauthenticated or no license errors
  if (error instanceof BrewManApiError) {
    if (error.isUnauthenticated) {
      return;
    }

    if (error.isPermissionDenied && error.reasonCode === "NoLicense") {
      return;
    }

    if (error.isPermissionDenied && error.reasonCode === "NoPermission") {
      // The message is the permission name
      const matchedError = EnumUserPermission.getValues().find(
        (permission) => (error.message.match(permission.value)?.length ?? 0) > 0
      );

      if (matchedError) {
        state.unauthorisedError = matchedError;
        return;
      }
    }
  }

  // Don't bother with failed to fetch errors
  if (
    error instanceof BrewManError &&
    error.message.includes("Failed to fetch")
  ) {
    return;
  }

  state.error = error;
}

/* eslint-enable no-console */

function clearUnauthorisedError() {
  state.unauthorisedError = null;
}

let snackbarTimer: ReturnType<typeof setTimeout> | null = null;

function scheduleSnackbarRemoval() {
  const current = state.snackbarConfigs[0];
  if (!current || snackbarTimer != null) return;

  snackbarTimer = setTimeout(() => {
    snackbarTimer = null;
    state.snackbarConfigs.shift();
    scheduleSnackbarRemoval(); // next head, if any
  }, current.timeout + 150); // Vuetify does a fade out after timeout, so we allow 150ms for the vuetify animation.
}

function pushSnackbarConfig(config: Partial<SnackbarConfiguration>) {
  state.snackbarConfigs.push(
    Object.assign(
      {
        id: Guid.create(),
        text: "",
        timeout: 2000,
        position: "bottom" as const,
        type: EnumSnackbarType.info,
      },
      config
    )
  );
  scheduleSnackbarRemoval(); // only starts a timer when none is running / or when this is the sole item after push
}

export default {
  state: readonly(state),
  setLoading,
  pushSnackbarConfig,
  setContextualHelpLink: (contextualHelpLink: string | undefined): void => {
    state.contextualHelpLink = contextualHelpLink ?? "";
  },
  forceRefresh: () => {
    state.appStateKey = Guid.create().toString();
  },
  showError,
  clearUnauthorisedError,
  setDocumentTitle: (title: string | null): void => {
    state.documentTitle = title
      ? title + $m.default_document_title_postfix
      : $m.default_document_title;
  },
  /**
   * Gets the appropriate help link for the current page (either contextual or general).
   */
  resolvedHelpLink: computed(
    () => state.contextualHelpLink || environment.helpPagesUrl
  ),
};

// Decorator for Telling AppStore to show loader
export function WithLoading<T extends Record<string, any>, R>(
  target: T,
  key: string | symbol,
  descriptor: TypedPropertyDescriptor<(...args: any[]) => R>
): void {
  const fn = descriptor.value as Function;
  descriptor.value = function (this: any, ...args) {
    let wasPromise = false;
    try {
      setLoading(true);

      const result = fn.apply(this, args);
      if (isPromise(result)) {
        wasPromise = true;
        result.finally(() => {
          setLoading(false);
        });
      }
      return result;
    } finally {
      if (!wasPromise) {
        setLoading(false);
      }
    }
  };
}

const isPromise = (p: any) => {
  return p && Object.prototype.toString.call(p) === "[object Promise]";
};
